import { CommunityTrust } from '@/components/ledger/CommunityTrust'
import { Link, createFileRoute, useLocation } from '@tanstack/react-router'
import { LedgerSection, LedgerSpecRow } from '@/components/ledger/LedgerBlocks'
import { COMPANY, LINKS } from '@/lib/company'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { localizedPageHead } from '@/lib/seo-head'
export const Route = createFileRoute('/{-$locale}/trust')({
  head: ({ params }) => localizedPageHead('trust', params.locale),
  component: Trust,
})
function Trust() {
  const locale = getLocaleFromPath(useLocation().pathname)
  const bs = locale === 'bs-BA'
  return (
    <div>
      <header className="ledger-wrap py-14">
        <h1 className="ledger-display text-[clamp(2.4rem,6vw,4.4rem)]">
          {bs ? 'Povjerenje počinje dokazima.' : 'Trust starts with evidence.'}
        </h1>
        <p className="ledger-lead mt-6">
          {bs
            ? 'Status naših proizvoda, izvori istraživanja i standard koji primjenjujemo na javne tvrdnje.'
            : 'Our product status, research sources, and the standard we apply to public claims.'}
        </p>
      </header>
      <LedgerSection title={bs ? 'Kompanija i kontakt' : 'Company and contact'}>
        <dl className="ledger-spec">
          <LedgerSpecRow term={bs ? 'Pravni subjekt' : 'Legal entity'}>
            {COMPANY.legalName}
          </LedgerSpecRow>
          <LedgerSpecRow term={bs ? 'Adresa' : 'Address'}>
            {COMPANY.street}, {COMPANY.postalCode} {COMPANY.locality}, Bosnia
            and Herzegovina
          </LedgerSpecRow>
          <LedgerSpecRow term={bs ? 'Kontakt' : 'Contact'}>
            <a className="ledger-link" href={`mailto:${COMPANY.email}`}>
              {COMPANY.email}
            </a>{' '}
            ·{' '}
            <a className="ledger-link" href={`tel:${COMPANY.phoneTel}`}>
              {COMPANY.phoneDisplay}
            </a>
          </LedgerSpecRow>
        </dl>
      </LedgerSection>
      <LedgerSection
        title={bs ? 'Status Horizon Clouda' : 'Horizon Cloud status'}
      >
        <p className="max-w-[70ch] leading-relaxed">
          {bs
            ? 'Horizon Cloud je u razvoju i još nije javno dostupan. Njemačka je planirana primarna regija; Švicarska je regija u uvođenju. Dostupnost usluga, lokacije obrade podataka i ugovorni uslovi moraju biti potvrđeni za svaki angažman. Cijene i nivoi usluge nisu javno objavljeni.'
            : 'Horizon Cloud is in development and is not yet publicly available. Germany is the intended primary region; Switzerland is a region in rollout. Service availability, data processing locations and contractual terms must be confirmed for each engagement. Pricing and service level commitments are not publicly published.'}
        </p>
        <Link
          className="ledger-link mt-5 inline-block"
          to={withLocalePath('/cloud', locale)}
        >
          {bs ? 'Pregled platforme' : 'Platform overview'}
        </Link>
      </LedgerSection>
      <LedgerSection title={bs ? 'Provjerljivi izvori' : 'Verifiable sources'}>
        <ul className="space-y-5">
          <li>
            <a className="ledger-link" href={LINKS.github}>
              Horizon Tech — {bs ? 'javni repozitoriji' : 'public repositories'}
            </a>
          </li>
          <li>
            <a className="ledger-link" href={LINKS.tilelangIssue}>
              {bs
                ? 'Prijavljeni problem u GPU kernelu'
                : 'Reported GPU kernel issue'}
            </a>
          </li>
          <li>
            <a className="ledger-link" href={LINKS.tilelangPr}>
              {bs
                ? 'Predloženi doprinos GPU kernelu'
                : 'Proposed GPU kernel contribution'}
            </a>
            <p className="ledger-muted mt-2">
              {bs
                ? 'Status prihvatanja provjerite u izvornom repozitoriju.'
                : 'Check acceptance status in the upstream repository.'}
            </p>
          </li>
          <li>
            <a className="ledger-link" href={LINKS.arkivedRepo}>
              arkived — {bs ? 'izvorni kod' : 'source code'}
            </a>
          </li>
        </ul>
      </LedgerSection>
      <LedgerSection title={bs ? 'Standard istraživanja' : 'Research standard'}>
        <p className="max-w-[70ch] leading-relaxed">
          {bs
            ? 'Rezultati performansi trebaju navesti hardver, verzije softvera, opterećenje, metodu, početne vrijednosti i ograničenja. Mjerenje hardvera ne predstavlja rezultat proizvodnog sistema. Tvrdnje trebaju imati provjerljive izvore i jasno naveden status istraživanja.'
            : 'Performance results should name hardware, software versions, workload, method, baselines and limitations. A hardware measurement does not establish a production system result. Claims should have verifiable sources and clearly stated research status.'}
        </p>
        <div className="mt-5 flex flex-wrap gap-5">
          <Link
            className="ledger-link"
            to={withLocalePath('/research', locale)}
          >
            {bs ? 'Naše istraživanje' : 'Our research'}
          </Link>
        </div>
      </LedgerSection>
      <CommunityTrust locale={locale} />
      <LedgerSection
        title={bs ? 'Privatnost i ispravke' : 'Privacy and corrections'}
      >
        <p className="max-w-[70ch] leading-relaxed">
          {bs
            ? 'Za ispravke, sigurnosne prijave i pitanja o obradi podataka kontaktirajte nas. Ne šaljite pristupne podatke niti povjerljive podatke putem javnih kanala.'
            : 'Contact us for corrections, security reports and questions about data handling. Keep credentials and confidential data out of public channels.'}
        </p>
        <div className="mt-5 flex gap-5">
          <Link className="ledger-link" to={withLocalePath('/privacy', locale)}>
            {bs ? 'Privatnost' : 'Privacy'}
          </Link>
          <Link className="ledger-link" to={withLocalePath('/terms', locale)}>
            {bs ? 'Uslovi' : 'Terms'}
          </Link>
        </div>
      </LedgerSection>
    </div>
  )
}
